<template>
  <div class="bg-white rounded-2xl p-7 border border-[#1c4848]/10 shadow-sm flex flex-col" :class="[wrapperClass]">
    <div class="flex items-center justify-between mb-6 gap-4">
      <div>
        <h3 class="text-[20px] font-medium tracking-tight text-[#1c4848]">{{ title }}</h3>
        <p class="text-[15px] text-[#1c4848]/55 mt-1">
          {{ subtitle }}
        </p>
      </div>
      <span v-if="rows.length" class="text-[13px] font-medium px-2.5 py-1 rounded-full text-[#1c4848]/70 bg-[#1c4848]/5 shrink-0">
        {{ formatNumber(grandTotal) }} {{ unit }}
      </span>
    </div>

    <p v-if="!rows.length" class="text-base text-[#1c4848]/40 py-8 text-center">
      {{ emptyText }}
    </p>

    <ol v-else class="space-y-5">
      <li v-for="(row, index) in visibleRows" :key="row.value" class="flex items-center gap-4">
        <span class="w-6 text-[14px] font-normal text-[#1c4848]/35 tabular-nums shrink-0">{{ index + 1 }}</span>
        <div class="flex-1 min-w-0">
          <div class="flex items-baseline justify-between gap-3">
            <span class="text-[16px] font-normal text-[#1c4848] truncate" :title="row.value">{{ labelFor(row.value) }}</span>
            <span class="text-[17px] font-medium text-[#1c4848] shrink-0 tabular-nums">
              {{ formatNumber(row.total) }}
              <span class="text-[13px] font-normal text-[#1c4848]/45 ml-1.5">
                {{ formatNumber(row.users) }} {{ row.users === 1 ? 'user' : 'users' }}
              </span>
            </span>
          </div>
          <div class="w-full h-1.5 rounded-full bg-[#1c4848]/[0.06] overflow-hidden mt-2">
            <div class="h-full rounded-full transition-all duration-500" :style="{ width: `${Math.max(share(row), 2)}%`, backgroundColor: color }" />
          </div>
        </div>
      </li>
    </ol>

    <button
      v-if="rows.length > limit"
      class="mt-5 self-start text-[15px] font-medium text-[#1c4848]/60 hover:text-[#1c4848] transition-colors"
      @click="expanded = !expanded"
    >
      {{ expanded ? 'Show less' : `Show all ${rows.length}` }}
    </button>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const props = defineProps({
  title: { type: String, required: true },
  subtitle: { type: String, default: '' },
  /** [{ value, total, users }] as the property-breakdown endpoint returns them. */
  rows: { type: Array, default: () => [] },
  labelFor: { type: Function, default: (value) => value },
  unit: { type: String, default: 'total' },
  emptyText: { type: String, default: 'Nothing recorded in this period yet.' },
  color: { type: String, default: '#1c4848' },
  limit: { type: Number, default: 8 },
  wrapperClass: { type: String, default: '' },
});

const expanded = ref(false);
const visibleRows = computed(() => (expanded.value ? props.rows : props.rows.slice(0, props.limit)));
const maxTotal = computed(() => props.rows.reduce((max, row) => Math.max(max, row.total || 0), 0));
const grandTotal = computed(() => props.rows.reduce((sum, row) => sum + (row.total || 0), 0));

function share(row) {
  return maxTotal.value ? (row.total / maxTotal.value) * 100 : 0;
}

function formatNumber(value) {
  return new Intl.NumberFormat('en-US').format(value ?? 0);
}
</script>
